import React, { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { sendContactMessage } from '@/services/firebaseService';
import { 
  Mail, Phone, MapPin, Clock, Send, CheckCircle, ArrowRight,
  Bot,
  Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast({
        title: "Erreur",
        description: t('contact.form.error'),
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    
    try {
      await sendContactMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        userId: null,
      });

      toast({
        title: t('contact.form.thankYou'),
        description: t('contact.form.success'),
        variant: "default",
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du message.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-background -z-10" />
          <div className="absolute top-40 right-10 w-72 h-72 rounded-full bg-accent/10 filter blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-accent/10 filter blur-3xl -z-10" />
          
          <div className="container px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-12 animated-element appear">
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6 text-foreground">
                {t('contact.title')}
              </h1>
              <p className="text-xl text-muted-foreground">
                {t('contact.subtitle')}
              </p>
              <p className="text-xl text-muted-foreground">
                {t('contact.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
              <div className="lg:col-span-1 space-y-8 animated-element appear">
                <div className="bg-card rounded-[2rem] shadow-sm p-8 border border-border">
                  <h3 className="text-xl font-display font-bold mb-6 text-foreground">
                    {t('contact.info.title')}
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center mr-4">
                        <Info className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('contact.helpInfo')}</p>
                        <p className="text-sm text-muted-foreground">{t('contact.info.help')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center mr-4">
                        <Mail className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Email</p>
                        <p className="text-sm text-muted-foreground">{t('contact.info.email')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary text-primary-foreground rounded-[2rem] shadow-sm p-8">
                  <h3 className="text-xl font-display font-bold mb-6">
                    {t('common.getStarted')}
                  </h3>
                  <p className="mb-6 opacity-90">
                    {t('contact.info.ready')}.
                  </p>
                  <div className="flex flex-col space-y-3">
                    <Button 
                      onClick={() => navigate('/register')} 
                      variant="outline" 
                      className="rounded-xl bg-accent text-white border-0 hover:bg-accent/90"
                    >
                      {t('common.register')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button 
                      onClick={() => navigate('/login')} 
                      variant="ghost" 
                      className="rounded-xl border border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10"
                    >
                      {t('common.login')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-2 animated-element appear">
                <div className="bg-card rounded-[2rem] shadow-sm p-8 border border-border">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium">
                          {t('contact.form.name')} <span className="text-accent">*</span>
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="rounded-xl border-border focus:border-accent"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          {t('contact.form.email')} <span className="text-accent">*</span>
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="rounded-xl border-border focus:border-accent"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="subject" className="text-sm font-medium">
                        {t('contact.form.subject')} <span className="text-accent">*</span>
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        required
                        className="rounded-xl border-border focus:border-accent"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-medium">
                        {t('contact.form.message')} <span className="text-accent">*</span>
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your message here..."
                        required
                        className="rounded-xl min-h-[179px] border-border focus:border-accent"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full md:w-auto rounded-xl px-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          {t('common.send')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Contact;